import { Button, DrawerCard, DrawerTitle } from "@mixitone/components";
import type { FundingOutreach } from "@mixitone/models";
import { ApplicationView } from "@mixitone/mvc";
import Form from "components/Forms/Form";
import FormButtons from "components/Forms/FormButtons";
import { DrawerFormFieldset } from "components/Forms/FormRenderers/DrawerFormRenderer";
import { FunderFormController } from "components/Pages/Funding/FunderFormController";
import { DrawerContextLink } from "components/common/DrawerContextLink";
import { RoutedDrawer } from "components/common/RoutedDrawer";
import { FundingOutreachStatusPill } from "components/Pages/Funding/FundingOutreachStatusPill";
import { injectModal } from "lib/injectParams";
import { useMemo } from "react";
import { Link } from "router";

const EditFunderDrawer = FunderFormController.scope(
  ApplicationView(() => {
    const controller = FunderFormController.use();
    const { loading, outreach, enrichmentStatus, enrichmentMessage, enrichmentError, enrichmentHadUpdates } =
      controller.state;
    const clubId = controller.props.clubId;
    const fields = useMemo(() => Object.values(controller.fields()), [controller]);

    if (loading) return <RoutedDrawer spinner={loading} />;

    return (
      <RoutedDrawer>
        <DrawerTitle contextLink={<DrawerContextLink context="funders" params={{ clubId }} />}>
          Edit funder
        </DrawerTitle>
        <Form name="funder">
          <div className="space-y-6">
            {controller.state.record?.funding_email_opted_out_club_wide_at ? (
              <div className="rounded border border-red-200/50 bg-red-950/40 px-3 py-2 text-sm text-red-200">
                This contact has <strong className="font-medium">unsubscribed from all funding emails</strong>{" "}
                from this club. Mixitone support can lift this if needed.
              </div>
            ) : null}
            <DrawerFormFieldset fields={fields} />
            {controller.state.record?.website?.trim() ? (
              <DrawerCard title="Enrich from website">
                <p className="mb-3 text-sm text-gray-600">
                  Pull contact details and a brief summary from the funder&apos;s website into this record.
                </p>
                {enrichmentMessage ? <p className="mb-3 text-sm text-gray-700">{enrichmentMessage}</p> : null}
                {enrichmentError ? <p className="mb-3 text-sm text-red-600">{enrichmentError}</p> : null}
                {enrichmentStatus === "completed" && enrichmentHadUpdates ? (
                  <p className="mb-3 text-sm text-green-700">
                    Enrichment complete. Check the fields above and correct anything that looks wrong before
                    saving.
                  </p>
                ) : null}
                <Button
                  type="button"
                  kind="secondary"
                  disabled={!controller.canEnrichFromWebsite}
                  onClick={() => controller.actionEnrichFromWebsite()}
                >
                  {enrichmentStatus === "running" ? "Enriching…" : "Enrich from website"}
                </Button>
              </DrawerCard>
            ) : null}
            <DrawerCard>
              <FormButtons saveTitle="Save" />
            </DrawerCard>
            {outreach.length > 0 && (
              <DrawerCard title="Appeals & outreach">
                <p className="mb-4 text-sm text-gray-600">
                  This funder has been added to the following appeals. Open an appeal to view or edit the
                  application.
                </p>
                <ul className="space-y-3">
                  {outreach.map((o: FundingOutreach) => {
                    const appeal = (o as any).funding_appeal;
                    const appealName = appeal?.name ?? "Unnamed appeal";
                    const appealId = o.funding_appeal_id;
                    const appealDate =
                      appeal?.created_at != null
                        ? new Date(appeal.created_at).toLocaleDateString(undefined, {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                          })
                        : null;
                    return (
                      <li
                        key={o.id}
                        className="flex flex-col gap-2 rounded-md border border-gray-200 bg-gray-50/80 px-3 py-2.5 text-sm"
                      >
                        <div className="flex flex-nowrap items-center gap-2">
                          <span className="min-w-0 flex-1 truncate font-medium text-gray-900">
                            {appealName}
                          </span>
                          <FundingOutreachStatusPill status={o.status} />
                          {appealDate && <span className="shrink-0 text-gray-500">{appealDate}</span>}
                        </div>
                        {appealId && clubId && (
                          <div className="border-t border-gray-200/80 pt-2">
                            <Link
                              to="/clubs/:clubId/funding/appeals/:appealId"
                              params={{ clubId, appealId }}
                              className="text-primary-600 hover:underline"
                            >
                              Open appeal
                            </Link>
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </DrawerCard>
            )}
          </div>
        </Form>
      </RoutedDrawer>
    );
  }),
);

export default injectModal(EditFunderDrawer);
